import { useEffect, useState } from 'react';
import { NavigateBefore } from '@mui/icons-material';
import NavigateNext from '@mui/icons-material/NavigateNext';
import { useSwipeable } from 'react-swipeable';
import { Attachments, CloudFile } from './FileViewer';
import {
  Box,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

interface ThumbnailsProps {
  attachments: Attachments | [];
  selected: any;
  onThumbnailSelect: (index: any) => void;
}

export const NEXT = 'NEXT';
export const PREV = 'PREV';
type Direction = typeof PREV | typeof NEXT;

export const Thumbnails: React.FC<ThumbnailsProps> = ({
  attachments,
  selected,
  onThumbnailSelect,
}) => {
  const [firstShownIndex, setFirstShownIndex] = useState(0 as number);
  const theme = useTheme();
  const smBelow = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (
      attachments
        .map(function (x: any) {
          return x.id;
        })
        .indexOf(selected.id) >=
      firstShownIndex + (smBelow ? 2 : 3)
    ) {
      setFirstShownIndex(
        () =>
          attachments
            .map(function (x: any) {
              return x.id;
            })
            .indexOf(selected.id) - (smBelow ? 1 : 2)
      );
    }

    if (
      firstShownIndex >= 1 &&
      attachments
        .map(function (x: any) {
          return x.id;
        })
        .indexOf(selected.id) <=
        firstShownIndex - 1
    ) {
      setFirstShownIndex((prevIndex) => prevIndex - 1);
    }
    // eslint-disable-next-line
  }, [selected, attachments, smBelow, setFirstShownIndex]);

  const slide = (dir: Direction) => {
    if (dir === NEXT && attachments[firstShownIndex + (smBelow ? 2 : 3)]) {
      setFirstShownIndex(firstShownIndex + 1);
      return;
    }
    if (dir === PREV && attachments[firstShownIndex - 1]) {
      setFirstShownIndex(firstShownIndex - 1);
      return;
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => slide(NEXT),
    onSwipedRight: () => slide(PREV),
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  return attachments.length <= 0 ? (
    <></>
  ) : (
    <Paper
      {...handlers}
      sx={{
        width: '100%',
        padding: '0px 65px 0px 65px',
        display: 'flex',
        flexWrap: 'wrap',
        maxWidth: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 'row',
        minHeight: '150px',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          paddingLeft: '25px',
        }}
      >
        <IconButton
          disabled={
            attachments[firstShownIndex - (smBelow ? 1 : 1)] ? false : true
          }
          onClick={() => slide(PREV)}
        >
          <NavigateBefore />
        </IconButton>
      </Box>
      {attachments.map((attachment, i) => {
        const payload = attachment?.payload as { attachment: CloudFile };

        return (
          <Box
            key={i}
            onClick={() => onThumbnailSelect(attachment)}
            sx={{
              margin: '3px',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              border: selected.id === attachment.id ? '2px solid' : 'none',
              borderColor: 'primary.main',
              display:
                i > firstShownIndex + (smBelow ? 1 : 2) ||
                i < firstShownIndex + (smBelow ? 0 : 0)
                  ? 'none'
                  : 'flex',
            }}
          >
            {payload.attachment.ext !== 'jpeg' &&
            payload.attachment.ext !== 'png' &&
            payload.attachment.ext !== 'jpg' &&
            payload.attachment.ext !== 'gif' ? (
              <Box
                key={i}
                sx={{
                  width: '100px',
                  minHeight: '100px',
                  maxHeight: '100px',
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <span
                  className={`fiv-viv fiv-icon-${payload.attachment.ext} fiv-size-lg`}
                  style={{ fontSize: '80px' }}
                ></span>
                <Typography
                  variant="caption"
                  align="center"
                  sx={{
                    marginTop: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100px',
                  }}
                >
                  {payload.attachment.originalname}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100px',
                  minHeight: '100px',
                  maxHeight: '100px',
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box
                  key={i}
                  component={'img'}
                  sx={{
                    verticalAlign: 'middle',
                    height: '100px',
                    lineHeight: 0,
                    maxWidth: '100px',
                    minWidth: '100px',
                  }}
                  src={payload.attachment.file}
                />
                <Typography
                  align="center"
                  variant="caption"
                  noWrap
                  sx={{
                    marginTop: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100px',
                  }}
                >
                  {payload.attachment.originalname}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          paddingRight: '25px',
        }}
      >
        <IconButton
          disabled={
            attachments[firstShownIndex + (smBelow ? 2 : 3)] ? false : true
          }
          onClick={() => slide(NEXT)}
        >
          <NavigateNext />
        </IconButton>
      </Box>
    </Paper>
  );
};
